import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import {
  updateQBInvoiceSmsOverage,
  chargeStripeCustomerForInvoice,
  sendQBInvoiceToCustomer,
  createQuickBooksPaymentForInvoice,
  updateQBInvoiceCustomerCountOverage,
  createQBMailerInvoiceWithOverage,
} from "../../../../shared/billing.api.js";
import dayjs from "dayjs";

// Single action modals and logic hook
export function useSingleInvoiceActions({ snackbarRef }) {
  // State for each modal
  const [confirmCreateAndOverage, setConfirmCreateAndOverage] = useState(null);
  const [createAndOverageLoading, setCreateAndOverageLoading] = useState(false);
  const [createAndOverageResult, setCreateAndOverageResult] = useState(null);

  const [confirmChargeCustomer, setConfirmChargeCustomer] = useState(null);
  const [chargeCustomerLoading, setChargeCustomerLoading] = useState(false);
  const [chargeCustomerResult, setChargeCustomerResult] = useState(null);

  const [confirmSendInvoice, setConfirmSendInvoice] = useState(null);
  const [sendInvoiceLoading, setSendInvoiceLoading] = useState(false);
  const [sendInvoiceResult, setSendInvoiceResult] = useState(null);

  const [confirmMarkPaid, setConfirmMarkPaid] = useState(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [markPaidResult, setMarkPaidResult] = useState(null);

  // Callback state for SMS overage
  const [createAndOverageCallback, setCreateAndOverageCallback] =
    useState(null);

  // Callback state for each action
  const [chargeCustomerCallback, setChargeCustomerCallback] = useState(null);
  const [sendInvoiceCallback, setSendInvoiceCallback] = useState(null);
  const [markPaidCallback, setMarkPaidCallback] = useState(null);

  // Add state for selected invoice
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  // Add state for editing
  const [isEditingOverage, setIsEditingOverage] = useState(false);
  const [editableOverage, setEditableOverage] = useState({
    overage_usage: "",
    overage_per_unit: "",
  });

  function getInvoiceById(row, invoiceId) {
    if (!row) return null;
    if (Array.isArray(row.invoices) && invoiceId) {
      return (
        row.invoices.find((inv) => inv.Id === invoiceId) || row.invoices[0]
      );
    }
    return row.invoice || null;
  }

  // Helper: check if action already done
  function hasAlreadyCreatedAndOverage(row, invoiceId) {
    // Use logs from the selected invoice if available
    let logs = row?.invoice_logs;
    if (Array.isArray(row?.invoices) && invoiceId) {
      const selectedInvoice = row.invoices.find((inv) => inv.Id === invoiceId);
      if (selectedInvoice?.invoice_logs) logs = selectedInvoice.invoice_logs;
    }
    return (
      logs?.some(
        (log) =>
          log.action === "create_qb_mailer_invoice" &&
          log.result_status === "success"
      ) ||
      (Array.isArray(row?.invoices) && row.invoices.length > 0)
    );
  }
  function hasAlreadyChargedCustomer(row, invoiceId) {
    let logs = row?.changelogs;
    if (Array.isArray(row?.invoices) && invoiceId) {
      const selectedInvoice = row.invoices.find((inv) => inv.Id === invoiceId);
      if (selectedInvoice?.changelogs) logs = selectedInvoice.changelogs;
    }
    return logs?.some(
      (log) =>
        log.action === "charge_stripe_customer" &&
        log.result_status === "success"
    );
  }
  function hasAlreadySentInvoice(row, invoiceId) {
    let invoice = row?.invoice;
    let logs = row?.changelogs;
    if (Array.isArray(row?.invoices) && invoiceId) {
      const selectedInvoice = row.invoices.find((inv) => inv.Id === invoiceId);
      if (selectedInvoice) {
        invoice = selectedInvoice;
        if (selectedInvoice.changelogs) logs = selectedInvoice.changelogs;
      }
    }
    return (
      invoice?.EmailStatus === "EmailSent" ||
      invoice?.EInvoiceStatus === "Sent" ||
      logs?.some(
        (log) =>
          log.result_status === "success" &&
          log.action.includes("send_customer_invoice")
      )
    );
  }
  function hasAlreadyMarkedPaid(row, invoiceId) {
    let invoice = row?.invoice;
    let logs = row?.changelogs;
    if (Array.isArray(row?.invoices) && invoiceId) {
      const selectedInvoice = row.invoices.find((inv) => inv.Id === invoiceId);
      if (selectedInvoice) {
        invoice = selectedInvoice;
        if (selectedInvoice.changelogs) logs = selectedInvoice.changelogs;
      }
    }
    return (
      invoice?.Balance === 0 ||
      logs?.some(
        (log) =>
          log.action === "create_quickbooks_payment" &&
          log.result_status === "success"
      )
    );
  }

  // Helper: check if previous step is completed (from logs)
  function isCreatedAndOverageCompleted(row, invoiceId) {
    return hasAlreadyCreatedAndOverage(row, invoiceId);
  }
  function isChargeCustomerCompleted(row, invoiceId) {
    return hasAlreadyChargedCustomer(row, invoiceId);
  }
  function isAlreadyMarkedPaid(row, invoiceId) {
    return hasAlreadyMarkedPaid(row, invoiceId);
  }

  // Helper to handle API responses and set result state
  function handleApiResponse(
    setLoading,
    setResult,
    setSnackbar,
    row,
    actionLabel
  ) {
    return (result) => {
      setLoading(false);
      const res = result?.data?.results?.[0] || result?.data?.results || result;
      setResult({
        status: "success",
        result: res,
      });
      setSnackbar?.({
        type: "success",
        message: `Successfully completed ${actionLabel} for ${getInvoiceDisplayName(
          row?.invoice
        )}`,
      });
      // Do NOT call callback here
    };
  }

  function handleApiError(
    setLoading,
    setResult,
    setSnackbar,
    row,
    actionLabel
  ) {
    return (e) => {
      setLoading(false);
      setResult({
        status: "error",
        error: e.message,
        raw: e,
      });
      setSnackbar?.({
        type: "error",
        message: `Error during ${actionLabel} : ${e.message} See Invoice status for details`,
      });
      // Do NOT call callback here
    };
  }

  useEffect(() => {
    if (confirmCreateAndOverage) {
      setEditableOverage({
        overage_usage: confirmCreateAndOverage?.overage_usage ?? "",
        overage_per_unit: confirmCreateAndOverage?.overage_per_unit ?? "",
      });
      setIsEditingOverage(false);
    }
  }, [confirmCreateAndOverage]);

  // --- Handlers ---

  function handleCreateAndOverage(row) {
    setCreateAndOverageLoading(true);
    setCreateAndOverageResult(null);
    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Creating QuickBooks Mailer Invoice and updating overage for ${
        confirmCreateAndOverage?.account_account_name ??
        confirmCreateAndOverage?.snowflake_account_name ??
        confirmCreateAndOverage?.qb_customer_display_name ??
        "--"
      }  )}`,
    });

    createQBMailerInvoiceWithOverage({
      quickbooks_customer_id: row.quickbooks_customer_id,
      start_date: dayjs(row.selectedMonth)
        .startOf("month")
        .format("YYYY-MM-DD"),
      mailer_dimension: "4x6", //todo Snowflake has no references to any other mailer types, confirm if this is correct
      overage_usage: isEditingOverage
        ? Number(editableOverage.overage_usage)
        : row.overage_usage,
      overage_per_unit: isEditingOverage
        ? Number(editableOverage.overage_per_unit)
        : row.overage_per_unit,
      account_id: row.account_id,
    })
      .then(
        handleApiResponse(
          setCreateAndOverageLoading,
          setCreateAndOverageResult,
          snackbarRef.current?.addSnackbar,
          row,
          "create mailer invoice and overage"
        )
      )
      .catch(
        handleApiError(
          setCreateAndOverageLoading,
          setCreateAndOverageResult,
          snackbarRef.current?.addSnackbar,
          row,
          "create mailer invoice and overage"
        )
      );
  }

  function handleChargeCustomer(row) {
    setChargeCustomerLoading(true);
    setChargeCustomerResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Charging customer for ${getInvoiceDisplayName(row?.invoice)}`,
    });
    chargeStripeCustomerForInvoice(row.invoice.Id)
      .then(
        handleApiResponse(
          setChargeCustomerLoading,
          setChargeCustomerResult,
          snackbarRef.current?.addSnackbar,
          row,
          "charge customer"
        )
      )
      .catch(
        handleApiError(
          setChargeCustomerLoading,
          setChargeCustomerResult,
          snackbarRef.current?.addSnackbar,
          row,
          "charge customer"
        )
      );
  }

  function handleSendInvoice(row) {
    setSendInvoiceLoading(true);
    setSendInvoiceResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Sending ${getInvoiceDisplayName(row?.invoice)} to customer`,
    });
    sendQBInvoiceToCustomer(row.invoice.Id)
      .then(
        handleApiResponse(
          setSendInvoiceLoading,
          setSendInvoiceResult,
          snackbarRef.current?.addSnackbar,
          row,
          "send invoice"
        )
      )
      .catch(
        handleApiError(
          setSendInvoiceLoading,
          setSendInvoiceResult,
          snackbarRef.current?.addSnackbar,
          row,
          "send invoice"
        )
      );
  }

  function handleMarkPaid(row) {
    console.log("handleMarkPaid called with row:", row);
    setMarkPaidLoading(true);
    setMarkPaidResult(null);

    snackbarRef.current?.addSnackbar({
      type: "info",
      message: `Marking ${getInvoiceDisplayName(row?.invoice)} as paid...`,
    });
    createQuickBooksPaymentForInvoice(row.invoice.Id)
      .then(
        handleApiResponse(
          setMarkPaidLoading,
          setMarkPaidResult,
          snackbarRef.current?.addSnackbar,
          row,
          "mark as paid"
        )
      )
      .catch(
        handleApiError(
          setMarkPaidLoading,
          setMarkPaidResult,
          snackbarRef.current?.addSnackbar,
          row,
          "mark as paid"
        )
      );
  }

  // --- Helper for result color and JSON dump ---
  function getResultColor(result) {
    if (!result) return "inherit";
    if (
      result.status === "success" ||
      result.status_code === 200 ||
      result.result?.status === "success"
    )
      return "success.main";
    return "error.main";
  }
  function getResultText(result) {
    if (!result) return "";
    if (
      result.status === "success" ||
      result.status_code === 200 ||
      result.result?.status === "success"
    ) {
      return typeof result.result === "object"
        ? JSON.stringify(result.result, null, 2)
        : String(result.result);
    }
    // Always dump JSON if not success
    return JSON.stringify(result, null, 2);
  }

  // --- Modals ---
  const MAX_RESULT_HEIGHT = 160;

  const modals = (
    <>
      {/* Create and Overage Confirmation Modal */}
      <Dialog
        open={!!confirmCreateAndOverage}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r == "backdropClick") {
            return;
          }
          setConfirmCreateAndOverage(null);
          setCreateAndOverageResult(null);
          if (createAndOverageCallback) {
            createAndOverageCallback(!!createAndOverageResult);
          }
          setCreateAndOverageCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Invoice Create and Overage</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to create a new invoice and add overage for{" "}
            {confirmCreateAndOverage?.account_account_name ??
              confirmCreateAndOverage?.snowflake_account_name ??
              confirmCreateAndOverage?.qb_customer_display_name ??
              "--"}
          </Typography>
          {hasAlreadyCreatedAndOverage(confirmCreateAndOverage) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This account has already had an invoice created and mail overage
                added. Are you sure you want to create another?
              </Typography>
            </Box>
          )}
          <Box mt={2}>
            {isEditingOverage ? (
              <TextField
                label="Mailer Overage Usage"
                type="number"
                size="small"
                fullWidth
                value={editableOverage.overage_usage}
                disabled={!!createAndOverageResult || createAndOverageLoading}
                onChange={(e) =>
                  setEditableOverage((prev) => ({
                    ...prev,
                    overage_usage: e.target.value,
                  }))
                }
                InputProps={{ readOnly: !isEditingOverage }}
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Mailer Overage Usage:
                {editableOverage.overage_usage || "--"}
              </Typography>
            )}

            {isEditingOverage ? (
              <TextField
                label="Mailer Overage Per Unit Cost"
                type="number"
                size="small"
                fullWidth
                value={editableOverage.overage_per_unit}
                disabled={!!createAndOverageResult || createAndOverageLoading}
                onChange={(e) =>
                  setEditableOverage((prev) => ({
                    ...prev,
                    overage_per_unit: e.target.value,
                  }))
                }
                InputProps={{ readOnly: !isEditingOverage }}
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Mailer Overage Per Unit Cost:
                {editableOverage.overage_per_unit || "--"}
              </Typography>
            )}

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Total Overage Cost:</strong>{" "}
              {Number(editableOverage.overage_usage) &&
              Number(editableOverage.overage_per_unit)
                ? `$${(
                    Number(editableOverage.overage_usage) *
                    Number(editableOverage.overage_per_unit)
                  ).toFixed(2)}`
                : "--"}
            </Typography>
            <Typography variant="body2">
              <strong>Mail Dimension: </strong>4x6
            </Typography>
            <Typography variant="body2">
              <strong>Invoice Date:</strong>{" "}
              {dayjs(confirmCreateAndOverage?.selectedMonth)
                .endOf("month")
                .format("MMMM D, YYYY")}
            </Typography>
          </Box>

          {createAndOverageLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">
                Creating Invoice and adding Overage...
              </Typography>
            </Stack>
          )}
          {createAndOverageResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(createAndOverageResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(createAndOverageResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
          }}
        >
          {/* Left-justified Edit button */}
          {!isEditingOverage && !createAndOverageResult && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsEditingOverage(true)}
            >
              Edit Overage
            </Button>
          )}

          {/* Right/centered group for Close and Confirm */}
          <Box
            sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto" }}
          >
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setConfirmCreateAndOverage(null);
                setCreateAndOverageResult(null);
                if (createAndOverageCallback) {
                  createAndOverageCallback(!!createAndOverageResult);
                }
                setCreateAndOverageCallback(null);
              }}
              disabled={createAndOverageLoading}
            >
              Close
            </Button>
            {!createAndOverageResult && (
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateAndOverage({
                    ...confirmCreateAndOverage,
                    overage_usage: isEditingOverage
                      ? Number(editableOverage.overage_usage)
                      : confirmCreateAndOverage?.overage_usage,
                    overage_per_unit: isEditingOverage
                      ? Number(editableOverage.overage_per_unit)
                      : confirmCreateAndOverage?.overage_per_unit,
                  });
                }}
                disabled={createAndOverageLoading}
              >
                Confirm
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Charge Customer Confirmation Modal */}
      <Dialog
        open={!!confirmChargeCustomer}
        onClose={(e, r) => {
          if (r == "backdropClick") {
            return;
          }
          setConfirmChargeCustomer(null);
          setChargeCustomerResult(null);
          setSelectedInvoiceId(null);
          if (chargeCustomerCallback)
            chargeCustomerCallback(!!chargeCustomerResult);
          setChargeCustomerCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Charge Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to charge the customer for{" "}
            {getInvoiceDisplayName(
              getInvoiceById(confirmChargeCustomer, selectedInvoiceId)
            )}
            ?
          </Typography>
          {/* Invoice selector if more than 1 invoice */}
          {Array.isArray(confirmChargeCustomer?.invoices) &&
            confirmChargeCustomer.invoices.length > 1 && (
              <Box mt={2}>
                <FormControl fullWidth>
                  <InputLabel id="select-invoice-label">
                    Select Invoice
                  </InputLabel>
                  <Select
                    labelId="select-invoice-label"
                    value={
                      selectedInvoiceId || confirmChargeCustomer.invoices[0]?.Id
                    }
                    label="Select Invoice"
                    onChange={(e) => setSelectedInvoiceId(e.target.value)}
                    disabled={chargeCustomerLoading || !!chargeCustomerResult}
                  >
                    {confirmChargeCustomer.invoices.map((inv) => (
                      <MenuItem key={inv.Id} value={inv.Id}>
                        {inv.DocNumber
                          ? `Doc #${inv.DocNumber} (${inv.Id})`
                          : `Invoice ID ${inv.Id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          {hasAlreadyChargedCustomer(
            confirmChargeCustomer,
            selectedInvoiceId
          ) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already been charged to the customer.
              </Typography>
            </Box>
          )}
          {/* Previous step warnings */}
          {!isCreatedAndOverageCompleted(
            confirmChargeCustomer,
            selectedInvoiceId
          ) && (
            <Box mt={2}>
              <Typography color="warning.main">
                <strong>Warning:</strong>No invoice has been created yet.
              </Typography>
            </Box>
          )}
          <Box mt={2}>
            {(() => {
              const invoice = getInvoiceById(
                confirmChargeCustomer,
                selectedInvoiceId
              );
              return (
                <>
                  <Typography variant="body2">
                    <strong>Company:</strong>{" "}
                    {invoice?.CustomerRef?.name ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Amount:</strong>{" "}
                    {typeof invoice?.TotalAmt === "number"
                      ? `$${invoice.TotalAmt.toFixed(2)}`
                      : "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>SF Mailer Overage:</strong>{" "}
                    {confirmChargeCustomer?.overage_usage ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Overage Per Unit Cost:</strong>{" "}
                    {confirmChargeCustomer?.overage_per_unit ?? "--"}
                  </Typography>
                </>
              );
            })()}
          </Box>
          {chargeCustomerLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">Charging customer...</Typography>
            </Stack>
          )}
          {chargeCustomerResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(chargeCustomerResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(chargeCustomerResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmChargeCustomer(null);
              setChargeCustomerResult(null);
              setSelectedInvoiceId(null);
              if (chargeCustomerCallback)
                chargeCustomerCallback(!!chargeCustomerResult);
              setChargeCustomerCallback(null);
            }}
            disabled={chargeCustomerLoading}
          >
            Close
          </Button>
          {!chargeCustomerResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                // Pass the selected invoice to the handler
                const invoice = getInvoiceById(
                  confirmChargeCustomer,
                  selectedInvoiceId
                );
                handleChargeCustomer({ ...confirmChargeCustomer, invoice });
              }}
              disabled={
                chargeCustomerLoading ||
                hasAlreadyChargedCustomer(
                  confirmChargeCustomer,
                  selectedInvoiceId
                )
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Mark Invoice as Paid Confirmation Modal */}
      <Dialog
        open={!!confirmMarkPaid}
        onClose={(e, r) => {
          if (r == "backdropClick") {
            return;
          }
          setConfirmMarkPaid(null);
          setMarkPaidResult(null);
          setSelectedInvoiceId(null); // <-- Add this
          if (markPaidCallback) markPaidCallback(!!markPaidResult);
          setMarkPaidCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Mark Invoice as Paid</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark{" "}
            {getInvoiceDisplayName(
              getInvoiceById(confirmMarkPaid, selectedInvoiceId)
            )}{" "}
            as paid?
          </Typography>
          {/* Invoice selector if more than 1 invoice */}
          {Array.isArray(confirmMarkPaid?.invoices) &&
            confirmMarkPaid.invoices.length > 1 && (
              <Box mt={2}>
                <FormControl fullWidth>
                  <InputLabel id="select-invoice-label">
                    Select Invoice
                  </InputLabel>
                  <Select
                    labelId="select-invoice-label"
                    value={selectedInvoiceId || confirmMarkPaid.invoices[0]?.Id}
                    label="Select Invoice"
                    onChange={(e) => setSelectedInvoiceId(e.target.value)}
                    disabled={markPaidLoading || !!markPaidResult}
                  >
                    {confirmMarkPaid.invoices.map((inv) => (
                      <MenuItem key={inv.Id} value={inv.Id}>
                        {inv.DocNumber
                          ? `Doc #${inv.DocNumber} (${inv.Id})`
                          : `Invoice ID ${inv.Id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          {hasAlreadyMarkedPaid(confirmMarkPaid, selectedInvoiceId) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already been marked as paid in QuickBooks.
              </Typography>
            </Box>
          )}
          {/* Previous step warnings */}
          {!isCreatedAndOverageCompleted(
            confirmMarkPaid,
            selectedInvoiceId
          ) && (
            <Box mt={2}>
              <Typography color="warning.main">
                <strong>Warning:</strong> SMS overage has not been updated yet.
                You may want to complete that action before marking as paid.
              </Typography>
            </Box>
          )}
          {!isChargeCustomerCompleted(confirmMarkPaid, selectedInvoiceId) && (
            <Box mt={2}>
              <Typography color="warning.main">
                <strong>Warning:</strong> Customer has not been charged yet. You
                may want to complete that action before marking as paid.
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            {(() => {
              const invoice = getInvoiceById(
                confirmMarkPaid,
                selectedInvoiceId
              );
              return (
                <>
                  <Typography variant="body2">
                    <strong>Company:</strong>{" "}
                    {invoice?.CustomerRef?.name ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Amount:</strong>{" "}
                    {typeof invoice?.TotalAmt === "number"
                      ? `$${invoice.TotalAmt.toFixed(2)}`
                      : "--"}
                  </Typography>
                </>
              );
            })()}
          </Box>
          {markPaidLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">
                Marking invoice as paid...
              </Typography>
            </Stack>
          )}
          {markPaidResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(markPaidResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(markPaidResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmMarkPaid(null);
              setMarkPaidResult(null);
              setSelectedInvoiceId(null); // <-- Add this
              if (markPaidCallback) markPaidCallback(!!markPaidResult);
              setMarkPaidCallback(null);
            }}
            disabled={markPaidLoading}
          >
            Close
          </Button>
          {!markPaidResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                const invoice = getInvoiceById(
                  confirmMarkPaid,
                  selectedInvoiceId
                );
                handleMarkPaid({ ...confirmMarkPaid, invoice });
              }}
              disabled={
                markPaidLoading || hasAlreadyMarkedPaid(confirmMarkPaid)
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Send Invoice Confirmation Modal */}
      <Dialog
        open={!!confirmSendInvoice}
        onClose={(e, r) => {
          if (r == "backdropClick") {
            return;
          }
          setConfirmSendInvoice(null);
          setSendInvoiceResult(null);
          setSelectedInvoiceId(null);
          if (sendInvoiceCallback) sendInvoiceCallback(!!sendInvoiceResult);
          setSendInvoiceCallback(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Send Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to send{" "}
            {getInvoiceDisplayName(
              getInvoiceById(confirmSendInvoice, selectedInvoiceId)
            )}{" "}
            to the customer?
          </Typography>
          {/* Invoice selector if more than 1 invoice */}
          {Array.isArray(confirmSendInvoice?.invoices) &&
            confirmSendInvoice.invoices.length > 1 && (
              <Box mt={2}>
                <FormControl fullWidth>
                  <InputLabel id="select-invoice-label">
                    Select Invoice
                  </InputLabel>
                  <Select
                    labelId="select-invoice-label"
                    value={
                      selectedInvoiceId || confirmSendInvoice.invoices[0]?.Id
                    }
                    label="Select Invoice"
                    onChange={(e) => setSelectedInvoiceId(e.target.value)}
                    disabled={sendInvoiceLoading || !!sendInvoiceResult}
                  >
                    {confirmSendInvoice.invoices.map((inv) => (
                      <MenuItem key={inv.Id} value={inv.Id}>
                        {inv.DocNumber
                          ? `Doc #${inv.DocNumber} (${inv.Id})`
                          : `Invoice ID ${inv.Id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          {hasAlreadySentInvoice(confirmSendInvoice, selectedInvoiceId) && (
            <Box mt={2}>
              <Typography color="warning.main">
                This invoice has already been sent to the customer.
              </Typography>
            </Box>
          )}

          {/* Previous step warnings */}
          {!isCreatedAndOverageCompleted(
            confirmSendInvoice,
            selectedInvoiceId
          ) && (
            <Box mt={2}>
              <Typography color="warning.main">
                <strong>Warning:</strong> SMS overage has not been updated yet.
                You may want to complete that action before sending the invoice.
              </Typography>
            </Box>
          )}
          {!isChargeCustomerCompleted(
            confirmSendInvoice,
            selectedInvoiceId
          ) && (
            <Box mt={2}>
              <Typography color="warning.main">
                <strong>Warning:</strong> Customer has not been charged yet. You
                may want to complete that action before sending the invoice.
              </Typography>
            </Box>
          )}
          {!isAlreadyMarkedPaid(confirmSendInvoice, selectedInvoiceId) && (
            <Box mt={2}>
              <Typography color="warning.main">
                <strong>Warning:</strong> Invoice has not been marked as paid
                yet. You may want to complete that action before sending the
                invoice.
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            {(() => {
              const invoice = getInvoiceById(
                confirmSendInvoice,
                selectedInvoiceId
              );
              const overages =
                confirmSendInvoice?.overage_usage !== undefined
                  ? {
                      usage_amount: confirmSendInvoice.overage_usage,
                      per_unit_cost: confirmSendInvoice.overage_per_unit,
                    }
                  : {};
              return (
                <>
                  <Typography variant="body2">
                    <strong>Company:</strong>{" "}
                    {invoice?.CustomerRef?.name ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Amount:</strong>{" "}
                    {typeof invoice?.TotalAmt === "number"
                      ? `$${invoice.TotalAmt.toFixed(2)}`
                      : "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>SF Mailer Overage:</strong>{" "}
                    {overages.usage_amount || "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Overage Per Unit Cost:</strong>{" "}
                    {overages.per_unit_cost || "--"}
                  </Typography>
                </>
              );
            })()}
          </Box>
          {sendInvoiceLoading && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2">Sending invoice...</Typography>
            </Stack>
          )}
          {sendInvoiceResult && (
            <Box mt={2}>
              <Typography
                variant="body2"
                sx={{
                  color: getResultColor(sendInvoiceResult),
                  maxHeight: MAX_RESULT_HEIGHT,
                  overflowY: "auto",
                  background: "#f5f5f5",
                  borderRadius: 1,
                  p: 1,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <strong>Result:</strong> {getResultText(sendInvoiceResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmSendInvoice(null);
              setSendInvoiceResult(null);
              setSelectedInvoiceId(null);
              if (sendInvoiceCallback) sendInvoiceCallback(!!sendInvoiceResult);
              setSendInvoiceCallback(null);
            }}
            disabled={sendInvoiceLoading}
          >
            Close
          </Button>
          {!sendInvoiceResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                // Pass the selected invoice to the handler
                const invoice = getInvoiceById(
                  confirmSendInvoice,
                  selectedInvoiceId
                );
                handleSendInvoice({ ...confirmSendInvoice, invoice });
              }}
              disabled={
                sendInvoiceLoading || hasAlreadySentInvoice(confirmSendInvoice)
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );

  function getInvoiceDisplayName(invoice) {
    return invoice?.DocNumber
      ? `QuickBooks Doc Number ${invoice.DocNumber}`
      : invoice?.Id
      ? `QuickBooks Invoice ID ${invoice.Id}`
      : "--";
  }

  // Callback-enabled setter for SMS overage modal
  function openCreateInvoiceAndOveragesModal(data, callback) {
    setConfirmCreateAndOverage(data);

    setCreateAndOverageCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  // Callback-enabled setter for charge customer modal
  function openChargeCustomerModal(data, callback) {
    if (Array.isArray(data?.invoices) && data.invoices.length > 0) {
      setSelectedInvoiceId(data.invoices[0].Id);
    }
    setConfirmChargeCustomer(data);
    setChargeCustomerCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  // Callback-enabled setter for send invoice modal
  function openSendInvoiceModal(data, callback) {
    if (Array.isArray(data?.invoices) && data.invoices.length > 0) {
      setSelectedInvoiceId(data.invoices[0].Id);
    }
    setConfirmSendInvoice(data);
    setSendInvoiceCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  // Callback-enabled setter for mark paid modal
  function openMarkPaidModal(data, callback) {
    if (Array.isArray(data?.invoices) && data.invoices.length > 0) {
      setSelectedInvoiceId(data.invoices[0].Id);
    }
    setConfirmMarkPaid(data);
    setMarkPaidCallback(() => callback);
    if (callback) callback(false); // Modal opened, no API action yet
  }

  return {
    openCreateInvoiceAndOveragesModal,
    openChargeCustomerModal,
    openSendInvoiceModal,
    openMarkPaidModal,
    modals,
  };
}
